import { ApiClient } from '@listening/api-contract/api-client-tag';
import { readyAudioStatusSet } from '@listening/api-contract/audio-statuses';
import type {
  ListAudioUrlParams,
  UpdateAudioPayload as UpdateAudioPayloadSchema,
} from '@listening/api-contract';
import type {
  AudioConversionSummary,
  DocumentType,
} from '@listening/api-contract/schemas';
import { Effect } from 'effect';

// Row shape. Derived from the API contract so adding a field to the backend
// automatically widens the row type.
export type AudioItem = typeof AudioConversionSummary.Type;

// —— Query keys ——

// Base prefix for EVERY audio cache (bounded lists + per-id detail/summary).
// The optimistic walk (db-interim/walk-audio-caches.ts) enumerates / cancels /
// invalidates by this prefix, which is correct precisely because all audio
// query keys are constructed inside the query layer (no arbitrary callers).
export const audioItemsBaseKey = ['audioItems'] as const;
export const audioItemDetailQueryKey = (audioOrSlug: string) =>
  ['audioItems', 'detail', audioOrSlug] as const;
export const audioItemSummaryQueryKey = (audioOrSlug: string) =>
  ['audioItems', 'summary', audioOrSlug] as const;
export const audioPageImageQueryKey = (audioOrSlug: string, page: number) =>
  ['audioItems', 'pageImage', audioOrSlug, page] as const;

// Per-id detail/summary freshness (24h, matching pre-migration). The detail
// payload (chapters / voice / alignment) is near-immutable once a conversion
// completes, so we cache hard: within a session it's effectively never
// refetched (no per-focus / per-mount spam), and the SSR `/doc/$docId` route
// won't refetch the loader-primed detail on hydration.
//
// Why 24h and NOT `Infinity`: client-initiated edits (rename / share / status)
// stay current regardless — they patch this cache in place and `invalidate` on
// settle (walk-audio-caches.ts), and invalidation refetches even past staleTime.
// The finite window exists only to self-heal OUT-OF-BAND backend changes (a
// re-synthesis / re-conversion / data fix the client never triggered): with
// `Infinity` those would show stale until a hard reload; at 24h they refresh on
// the next focus/mount past the window.
export const AUDIO_DETAIL_STALE_TIME_MS = 24 * 60 * 60 * 1000;

// —— Effect programs (callable from any runtime that provides ApiClient) ——

type ListAudioParams = typeof ListAudioUrlParams.Type;

// Single-page fetch (one network call). Useful for cases where we explicitly
// don't want to drain the cursor (rare — most callsites want the full set).
const listAudioItemsPageEffect = (params: ListAudioParams = {}) =>
  Effect.flatMap(ApiClient, (api) =>
    // doc_pack_progress signals this client can decode doc-pack progress anchors
    // (null legacy chapter_id/tts_id) in the list rows.
    api.Audio.ListAudio({
      query: { ...params, doc_pack_progress: true },
    }).pipe(
      Effect.map((r) => ({
        items: [...r.audio_conversions],
        nextCursor: r.cursor ?? undefined,
        ttsFinalizedVersion: r.tts_finalized_version,
        voiceDataVersion: r.voice_data_version,
      })),
    ),
  ).pipe(
    Effect.withSpan('audio.list.page', {
      attributes: {
        'audio.sort_by': params.sort_by ?? 'created_at',
        'audio.list.processing_queue': params.except_status !== undefined,
      },
    }),
  );

// NOTE: the old full-drain `listAudioItemsEffect` (cursor-follow until
// exhausted) + `audioListQueryArgs` single-cache args were removed — the bounded
// named queries in db-interim/audio-list-hooks.ts page via
// `listAudioItemsPageEffect` instead. See the doc's "Scaling problem + decision".

// —— Derived selectors (pure; shared so web / RN / browser-ext derive
//    identically) ——

const CONTINUE_LISTENING_LIMIT = 10;

// "Continue listening" list, derived client-side from the full audio list.
// Reproduces the legacy `byPlayedAt` API query — which listed
// `{ sort_by: 'played_at', status: readyAudioStatuses, limit: 10 }`, ordered
// server-side by `progressUpdatedAt desc` (see ListAudio in
// apps/api/src/api-impl/audio-endpoints.ts):
//
//   - ready/playable only (`readyAudioStatusSet`, the old `status` filter),
//   - actually started (`progress.timestamp_ms !== 0`, the old client guard),
//   - sorted by recency via `progress.updated_at_ms` (the row mirror of the
//     server's `progressUpdatedAt` / `played_at` key). NOTE: `timestamp_ms` is
//     the playback *position within the chapter*, NOT recency — sorting by it
//     would order "furthest into a track" instead of "most recently played".
//   - capped at CONTINUE_LISTENING_LIMIT.
const selectContinueListening = (
  rows: ReadonlyArray<AudioItem>,
): ReadonlyArray<AudioItem> =>
  rows
    .filter(
      (a) =>
        a.progress &&
        a.progress.timestamp_ms !== 0 &&
        readyAudioStatusSet.has(a.conversion_status),
    )
    .toSorted(
      (a, b) =>
        (b.progress?.updated_at_ms ?? 0) - (a.progress?.updated_at_ms ?? 0),
    )
    .slice(0, CONTINUE_LISTENING_LIMIT);

const detailAudioItemEffect = (audioOrSlug: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Audio.GetAudio({
      params: { audioOrSlug },
      // Doc-pack-capable client: can decode doc-pack progress anchors (null
      // legacy chapter_id/tts_id). The prod Flutter app omits this. See
      // queryProgressSingle.
      query: { doc_pack_progress: true },
    }).pipe(Effect.map((r) => r.audio_conversion)),
  );

const detailedAudioItemEffect = (audioOrSlug: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Audio.GetAudioDetailed({
      params: { audioOrSlug },
      // Signal that this client can decode doc-pack progress anchors (null
      // legacy chapter_id/tts_id). The prod Flutter app omits this and gets
      // legacy chapter-anchored progress instead. See GetAudioDetailed handler.
      query: { doc_pack_progress: true },
    }).pipe(Effect.map((r) => r.audio_conversion)),
  ).pipe(
    Effect.withSpan('audio.detail.load', {
      attributes: { 'audio.id': audioOrSlug },
    }),
  );

const pageImageEffect = (audioOrSlug: string, page: number) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Audio.GetAudioPageImage({ params: { audioOrSlug, page } }),
  );

// Mirrors the UpdateAudio endpoint's payload shape, derived from the contract.
export type UpdateAudioPayload = typeof UpdateAudioPayloadSchema.Type;

const updateAudioItemEffect = (audioId: string, payload: UpdateAudioPayload) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Audio.UpdateAudio({
      params: { audioId },
      // doc_pack_progress signals this client can decode the doc-pack progress
      // anchors in the response (null legacy chapter_id/tts_id).
      payload: { ...payload, doc_pack_progress: true },
    }).pipe(Effect.map((r) => r.audio_conversion)),
  );

const deleteAudioItemEffect = (audioId: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Audio.DeleteAudio({ params: { audioId } }),
  );

const unarchiveAudioEffect = (audioId: string) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Audio.UnarchiveAudio({ payload: { audioId } }),
  );

export type CreateAudioByUrlPayload = {
  document_url: string;
  document_type?: DocumentType | undefined;
  title?: string | undefined;
  folder_id?: string | null | undefined;
  // Dev/staging-only per-request pipeline override; omitted in production.
  conversion_pipeline_version?: 'v1' | 'v2';
  // Dev/staging-only: run this conversion without the vision layout model; omitted in production.
  conversion_vision_off?: boolean;
  // Dev/staging-only v2 variant switches (no-ops on v1); omitted in production.
  conversion_ocr_provider?: 'azure' | 'paddle-modal' | 'paddleocr-vl-modal';
  conversion_label_model?: 'finetune' | 'general';
  conversion_update_mode?: 'finetune' | 'general' | 'deterministic';
  conversion_word_enrichment?: boolean;
};

function withoutUndefinedValues<T extends object>(value: T): T {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  ) as T;
}

const createAudioByUrlEffect = (payload: CreateAudioByUrlPayload) =>
  Effect.flatMap(ApiClient, (api) =>
    api.Audio.CreateAudio({ payload: withoutUndefinedValues(payload) }).pipe(
      Effect.map((r) => r.audio_conversion),
    ),
  );

// —— Public namespace ——
//
// The Effect programs + derived selectors are grouped under one `AudioItems`
// object instead of loose top-level exports: consumers write
// `AudioItems.detailEffect(id)` / `AudioItems.selectContinueListening(rows)`,
// which keeps these generically-named helpers out of the global import
// namespace. (Query keys + row/payload TYPES stay top-level — they're
// identifiers/types, not behavior, and the optimistic walk imports the keys
// directly.) These programs live in the shared query layer by design so
// start / browser-ext / RN all derive identically — see this package's
// CLAUDE.md.
export const AudioItems = {
  listPageEffect: listAudioItemsPageEffect,
  detailEffect: detailAudioItemEffect,
  detailedEffect: detailedAudioItemEffect,
  pageImageEffect,
  updateEffect: updateAudioItemEffect,
  deleteEffect: deleteAudioItemEffect,
  unarchiveEffect: unarchiveAudioEffect,
  createByUrlEffect: createAudioByUrlEffect,
  selectContinueListening,
  CONTINUE_LISTENING_LIMIT,
} as const;
